import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { errorMessage } from '../../services/api';
import { useAuth } from '../../services/auth';
import { toLocalInput, fromLocalInput, fmtDateTime } from '../../components/ui';
import { APP_NAME, C, LOGO } from '../../brand';
import { EVENT_LABELS, describe } from './common';

const panel = { background: '#ffffff', borderRadius: '16px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' };
const th = { padding: '16px', textAlign: 'left', color: '#555', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase' };
const td = { padding: '16px', color: '#333', fontSize: '14px' };
const primaryBtn = { padding: '12px 24px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' };
const editBtn = { padding: '6px 12px', background: '#e3f2fd', color: '#1565c0', border: '1px solid #bbdefb', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
const delBtn = { padding: '6px 12px', background: 'rgba(255,77,79,0.08)', color: '#cf1322', border: '1px solid rgba(255,77,79,0.3)', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 };
const cancelBtn = { flex: 1, padding: '12px 24px', background: '#f4f6f9', color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
const submitBtn = { flex: 1, padding: '12px 24px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
const modalBack = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' };
const modalBox = { background: '#ffffff', borderRadius: '16px', padding: '32px', width: '100%', maxWidth: '550px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.15)' };
const fieldLabel = { display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#333' };
const input = { width: '100%', padding: '12px 16px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', color: '#1a1a1a', background: '#fff', outline: 'none' };

const MENU = [
  { id: 'dashboard', label: 'Dashboard', icon: 'fa-chart-line' },
  { id: 'sessions', label: 'Sessions', icon: 'fa-calendar-alt' },
  { id: 'pdvs', label: 'Points de Vente', icon: 'fa-store' },
  { id: 'stocks', label: 'Stocks', icon: 'fa-boxes', external: true },
  { id: 'quotas', label: 'Quotas Journaliers', icon: 'fa-calendar-day', external: true },
  { id: 'prizes', label: 'Lots', icon: 'fa-gift' },
  { id: 'participants', label: 'Participants', icon: 'fa-users', external: true },
  { id: 'users', label: 'Utilisateurs', icon: 'fa-user-cog' },
  { id: 'products', label: 'Produits', icon: 'fa-bottle-water' },
  { id: 'journal', label: 'Journal', icon: 'fa-list-ul' },
  { id: 'settings', label: 'Réglages', icon: 'fa-sliders' },
];

const getColor = (idx) => ['#1890ff', '#52c41a', '#faad14', '#f5222d', '#722ed1', '#eb2f96', '#13c2c2'][idx % 7];

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(() => {
    try { return sessionStorage.getItem('palet_admin_tab') || 'dashboard'; } catch { return 'dashboard'; }
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const selectTab = (id) => {
    setActiveTab(id);
    setMessage({ type: '', text: '' });
    try { sessionStorage.setItem('palet_admin_tab', id); } catch { /* ignore */ }
  };
  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div className="admin-layout" style={{ display: 'flex', minHeight: '100vh', background: '#f4f6f9' }}>
      <Sidebar activeTab={activeTab} setActiveTab={selectTab} onLogout={handleLogout} user={user} />
      <MainContent activeTab={activeTab} message={message} setMessage={setMessage} />
    </div>
  );
}

function Sidebar({ activeTab, setActiveTab, onLogout, user }) {
  const navigate = useNavigate();
  const handleMenuClick = (item) => (item.external ? navigate(`/admin/${item.id}`) : setActiveTab(item.id));
  return (
    <div className="admin-sidebar" style={{ width: '250px', flexShrink: 0, background: 'rgba(13, 71, 161, 0.95)', minHeight: '100vh', display: 'flex', flexDirection: 'column', borderRight: `1px solid ${C.border}` }}>
      <div style={{ padding: '24px', borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
          <div style={{ width: '70px', height: '70px', borderRadius: '50%', background: '#ffffff', overflow: 'hidden', padding: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <img src={LOGO} alt="Supermont" style={{ width: '100%', height: '100%', objectFit: 'contain', borderRadius: '50%' }} />
          </div>
        </div>
        <p style={{ color: '#fff', margin: '4px 0 0', fontWeight: 'bold', textAlign: 'center', fontSize: '14px' }}>{APP_NAME}</p>
        <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: 0, fontSize: '11px', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '1px' }}>Administration</p>
      </div>
      <div className="menu" style={{ padding: '16px', flex: 1 }}>
        {MENU.map((item) => {
          const active = activeTab === item.id;
          return (
            <button key={item.id} onClick={() => handleMenuClick(item)} data-menu={item.id}
              style={{ width: '100%', padding: '12px 16px', border: 'none', background: active ? 'rgba(66, 165, 245, 0.25)' : 'transparent', textAlign: 'left', color: active ? C.accent : 'rgba(255, 255, 255, 0.75)', cursor: 'pointer', borderRadius: '8px', marginBottom: '8px', fontSize: '14px', fontWeight: 500, transition: 'all 0.2s', display: 'flex', alignItems: 'center', gap: '12px', borderLeft: active ? `3px solid ${C.accent}` : '3px solid transparent' }}
              onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgba(66, 165, 245, 0.1)'; }}
              onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}>
              <i className={`fas ${item.icon}`} style={{ width: '20px', textAlign: 'center' }}></i>
              {item.label}
            </button>
          );
        })}
        <button onClick={() => navigate('/hotesse')}
          style={{ width: '100%', padding: '10px 16px', background: 'rgba(255, 255, 255, 0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.35)', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}>
          <i className="fas fa-mobile-alt"></i>Écran hôtesse
        </button>
        <button onClick={onLogout}
          style={{ width: '100%', padding: '10px 16px', background: 'rgba(255, 255, 255, 0.04)', color: '#ff4d4f', border: '1px solid rgba(255, 77, 79, 0.7)', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '8px' }}
          onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255, 77, 79, 0.12)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255, 255, 255, 0.04)'; }}>
          <i className="fas fa-sign-out-alt"></i>Déconnexion
        </button>
      </div>
      <div style={{ padding: '16px', borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ffffff', fontSize: '18px', fontWeight: 600 }}>
            {(user?.username || 'A').charAt(0).toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#ffffff', fontSize: '14px' }}>{user?.username || 'Admin'}</div>
            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>{user?.role || ''}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function MessageBar({ message }) {
  if (!message.text) return null;
  const error = message.type === 'error';
  return (
    <div role="status" style={{ padding: '12px 24px', background: error ? '#fff2f0' : '#f6ffed', border: `1px solid ${error ? '#ffccc7' : '#b7eb8f'}`, borderRadius: '8px', marginBottom: '24px', color: error ? '#cf1322' : '#389e0d', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
      <i className={`fas fa-${error ? 'exclamation-circle' : 'check-circle'}`}></i>{message.text}
    </div>
  );
}

function PageHead({ icon, title, children }) {
  return (
    <div style={{ ...panel, padding: '24px 32px', marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <div style={{ width: '40px', height: '40px', background: C.gradient, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <i className={`fas ${icon}`} style={{ color: '#ffffff', fontSize: '20px' }}></i>
        </div>
        <h1 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#1a1a1a' }}>{title}</h1>
      </div>
      {children}
    </div>
  );
}

function MainContent({ activeTab, message, setMessage }) {
  const views = {
    sessions: SessionsContent, pdvs: PDVsContent, prizes: PrizesContent, users: UsersContent,
    products: ProductsContent, journal: JournalContent, settings: SettingsContent,
  };
  const View = views[activeTab] || DashboardContent;
  return (
    <div className="admin-main">
      <MessageBar message={message} />
      <View setMessage={setMessage} />
    </div>
  );
}

// ============ DASHBOARD ============

function DashboardContent({ setMessage }) {
  const [stats, setStats] = useState(null);
  const [hourlyData, setHourlyData] = useState(null);
  const [selectedCity, setSelectedCity] = useState('all');
  const [selectedDate, setSelectedDate] = useState('all');
  const [selectedPOS, setSelectedPOS] = useState('all');

  const getFromDate = (preset) => {
    if (preset === 'all' || !preset) return null;
    const today = new Date();
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (preset === 'today') return fmt(today);
    if (preset === 'week') { const d = new Date(today); d.setDate(d.getDate() - 7); return fmt(d); }
    if (preset === 'month') { const d = new Date(today); d.setMonth(d.getMonth() - 1); return fmt(d); }
    if (preset === 'custom') return fmt(today);
    return preset;
  };

  useEffect(() => {
    const params = {};
    if (selectedCity !== 'all') params.city_id = parseInt(selectedCity, 10);
    if (selectedDate !== 'all') params.date_from = selectedDate;
    if (selectedPOS !== 'all') params.pdv_id = parseInt(selectedPOS, 10);
    api.get('/palet/admin/stats/dashboard', { params }).then((r) => setStats(r.data))
      .catch(() => setMessage({ type: 'error', text: 'Erreur lors du chargement des statistiques' }));
  }, [selectedCity, selectedDate, selectedPOS, setMessage]);

  useEffect(() => {
    const params = {};
    if (selectedCity !== 'all') params.city_id = parseInt(selectedCity, 10);
    api.get('/palet/admin/stats/hourly', { params }).then((r) => setHourlyData(r.data)).catch(() => {});
  }, [selectedCity]);

  const selectStyle = { padding: '8px 16px', border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '14px', color: '#333', background: '#ffffff', cursor: 'pointer', outline: 'none' };
  const isDate = typeof selectedDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate);
  const pdvOptions = (stats?.pdvs || []).filter((p) => selectedCity === 'all' || String(p.city_id) === String(selectedCity));

  return (
    <>
      <PageHead icon="fa-bullseye" title={`Dashboard — ${APP_NAME}`}>
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={selectedCity} onChange={(e) => { setSelectedCity(e.target.value); setSelectedPOS('all'); }} style={selectStyle} aria-label="Ville">
            <option value="all">Toutes les villes</option>
            {stats?.cities?.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={isDate ? 'custom' : selectedDate} onChange={(e) => setSelectedDate(e.target.value === 'all' ? 'all' : getFromDate(e.target.value))} style={selectStyle} aria-label="Période">
            <option value="all">Toutes les dates</option>
            <option value={getFromDate('today')}>Aujourd'hui</option>
            <option value={getFromDate('week')}>Cette semaine</option>
            <option value={getFromDate('month')}>Ce mois</option>
            <option value="custom">Date spécifique</option>
          </select>
          {isDate && <input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value || 'all')} style={selectStyle} />}
          <select value={selectedPOS} onChange={(e) => setSelectedPOS(e.target.value)} style={selectStyle} aria-label="Point de vente">
            <option value="all">Tous les points de vente</option>
            {pdvOptions.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
      </PageHead>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '24px', marginBottom: '24px' }}>
        <KPICard title="Participants" value={stats?.summary?.total_participations || 0} icon="fa-user-group" color="#1890ff" />
        <KPICard title="Gagnants" value={stats?.summary?.total_winners || 0} icon="fa-medal" color="#52c41a" />
        <KPICard title="Lots Gagnés" value={stats?.summary?.total_prizes || 0} icon="fa-gift" color="#faad14"
          sub={stats ? `${stats.summary.total_delivered} remis` : ''} />
        <KPICard title="Bouteilles Vendues" value={stats?.summary?.total_bottles || 0} icon="fa-bottle-water" color="#eb2f96" />
        <KPICard title="Points de Vente Actifs" value={stats?.summary?.active_points || 0} icon="fa-store" color="#722ed1" />
      </div>

      <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
        <PrizeDistributionSection stats={stats} />
        <ParticipationModeSection stats={stats} />
      </div>

      <PerformanceSection stats={stats} />
      <CommercialPressureSection hourlyData={hourlyData} />
    </>
  );
}

function SectionTitle({ icon, color, title }) {
  return (
    <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '40px', height: '40px', background: `${color}26`, borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={`fa-solid ${icon}`} style={{ color, fontSize: '18px' }}></i>
      </div>
      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#1a1a1a' }}>{title}</h3>
    </div>
  );
}

const KPICard = ({ title, value, icon, color, sub }) => (
  <div className="kpi-card" style={{ ...panel, padding: '20px' }}>
    <div style={{ width: '52px', height: '52px', background: `${color}20`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px' }}>
      <i className={`fa-solid ${icon}`} style={{ color, fontSize: '24px' }}></i>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px' }}>{value.toLocaleString('fr-FR')}</div>
    <div style={{ fontSize: '13px', color: '#555', fontWeight: 500 }}>{title}{sub ? <span style={{ color: '#999' }}> · {sub}</span> : null}</div>
  </div>
);

const PrizeDistributionSection = ({ stats }) => {
  const entries = Object.entries(stats?.prizes || {});
  return (
    <div style={{ ...panel, padding: '24px' }}>
      <SectionTitle icon="fa-gift" color="#722ed1" title="Répartition des Lots" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {entries.length === 0 && <div style={{ color: '#999', fontSize: '14px' }}>Aucun lancer joué.</div>}
        {entries.map(([label, data], idx) => {
          const color = data.is_losing ? '#8c8c8c' : getColor(idx);
          return (
            <div key={label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px', background: '#f4f6f9', borderRadius: '12px', border: '1px solid #dee2e6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '44px', height: '44px', background: `${color}20`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={`fa-solid ${data.is_losing ? 'fa-circle-xmark' : 'fa-gift'}`} style={{ color, fontSize: '20px' }}></i>
                </div>
                <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{label}</div>
              </div>
              <div style={{ fontSize: '20px', fontWeight: 700, color }}>{data.won}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ParticipationModeSection = ({ stats }) => (
  <div style={{ ...panel, padding: '24px' }}>
    <SectionTitle icon="fa-share-nodes" color="#1890ff" title="Mode de Participation" />
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <ModeCard icon="fab fa-whatsapp" label="WhatsApp" value={stats?.engagement?.whatsapp || 0} rate={stats?.engagement?.whatsapp_rate || 0} color="#25D366" />
      <ModeCard icon="fa-solid fa-mobile-screen" label="Direct" value={stats?.engagement?.direct || 0} rate={stats?.engagement?.direct_rate || 0} color="#1890ff" />
      <ModeCard icon="fa-solid fa-qrcode" label="QR Code" value={stats?.engagement?.qr_code || 0} rate={stats?.engagement?.qr_code_rate || 0} color="#FF6B35" />
    </div>
  </div>
);

const ModeCard = ({ icon, label, value, rate, color }) => (
  <div style={{ padding: '16px', background: '#f4f6f9', borderRadius: '12px', border: '1px solid #dee2e6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
      <div style={{ width: '44px', height: '44px', background: `${color}20`, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <i className={icon} style={{ color, fontSize: '22px' }}></i>
      </div>
      <div>
        <div style={{ fontSize: '14px', fontWeight: 600, color: '#1a1a1a' }}>{label}</div>
        <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>{rate}% du total</div>
      </div>
    </div>
    <div style={{ textAlign: 'right' }}>
      <div style={{ fontSize: '20px', fontWeight: 700, color }}>{value}</div>
      <div style={{ fontSize: '12px', color: '#666' }}>Participations</div>
    </div>
  </div>
);

const PerformanceSection = ({ stats }) => (
  <div style={{ ...panel, padding: '24px', marginBottom: '24px' }}>
    <SectionTitle icon="fa-store" color="#faad14" title="Performance par Point de Vente" />
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #dee2e6' }}>
            {['Point de Vente', 'Ville', 'Inscrits', 'Gagnants', 'Sans lot', 'Bouteilles', 'Taux de jeu'].map((h, i) => (
              <th key={h} style={{ ...th, textAlign: i < 2 ? 'left' : 'center' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {(stats?.by_pos || []).length === 0 && (
            <tr><td colSpan="7" style={{ ...td, textAlign: 'center', color: '#999' }}>Aucune participation.</td></tr>
          )}
          {(stats?.by_pos || []).map((pos) => (
            <tr key={pos.id} style={{ borderBottom: '1px solid #dee2e6' }}>
              <td style={{ ...td, color: '#1a1a1a', fontWeight: 500 }}>{pos.name}</td>
              <td style={{ ...td, color: '#555' }}>{pos.city}</td>
              <td style={{ ...td, textAlign: 'center', color: '#1890ff', fontWeight: 600 }}>{pos.inscrits}</td>
              <td style={{ ...td, textAlign: 'center', color: '#52c41a', fontWeight: 600 }}>{pos.gagnants}</td>
              <td style={{ ...td, textAlign: 'center', color: '#ff7875', fontWeight: 600 }}>{pos.perdants}</td>
              <td style={{ ...td, textAlign: 'center', color: '#faad14', fontWeight: 600 }}>{pos.bouteilles}</td>
              <td style={{ ...td, textAlign: 'center' }}>
                <span title="Lancers joués / lancers ouverts" style={{ padding: '6px 16px', borderRadius: '20px', background: pos.performance >= 80 ? 'rgba(82, 196, 26, 0.15)' : pos.performance >= 50 ? 'rgba(250, 173, 20, 0.15)' : 'rgba(255, 77, 79, 0.15)', color: pos.performance >= 80 ? '#389e0d' : pos.performance >= 50 ? '#d4930a' : '#cf1322', fontWeight: 600, fontSize: '14px', display: 'inline-block' }}>
                  {pos.performance}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

function CommercialPressureSection({ hourlyData }) {
  const [selectedPDV, setSelectedPDV] = useState('all');
  const [selectedHour, setSelectedHour] = useState('all');
  const dates = [...(hourlyData?.all_dates || [])].sort().reverse();
  const pdvs = (hourlyData?.by_pdv || []).filter((p) => selectedPDV === 'all' || p.pdv_id === parseInt(selectedPDV, 10));
  const hourOk = (h) => selectedHour === 'all' || h === parseInt(selectedHour, 10);
  const rows = pdvs.map((p) => {
    const totals = {};
    dates.forEach((d) => { totals[d] = (p.dates?.[d]?.hourly || []).filter((h) => hourOk(h.hour)).reduce((s, h) => s + h.bottles, 0); });
    return { id: p.pdv_id, name: p.pdv_name, totals, total: Object.values(totals).reduce((a, b) => a + b, 0) };
  });
  const colTotals = Object.fromEntries(dates.map((d) => [d, rows.reduce((s, r) => s + (r.totals[d] || 0), 0)]));
  const grand = Object.values(colTotals).reduce((a, b) => a + b, 0);
  const thStyle = { padding: '14px 8px', textAlign: 'center', background: C.gradient, color: '#fff', minWidth: '60px', fontWeight: 600, fontSize: '13px' };
  const sel = { padding: '8px 12px', background: '#fff', border: '1px solid #dee2e6', borderRadius: '8px', color: '#333', fontSize: '13px' };
  const todayStr = new Date().toDateString();

  return (
    <div style={{ ...panel, padding: '24px' }}>
      <SectionTitle icon="fa-chart-line" color="#13c2c2" title="Pression Commerciale (bouteilles)" />
      <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', flexWrap: 'wrap' }}>
        <select value={selectedHour} onChange={(e) => setSelectedHour(e.target.value)} style={sel} aria-label="Heure">
          <option value="all">Toutes les heures</option>
          {Array.from({ length: 14 }, (_, i) => i + 8).map((h) => <option key={h} value={h}>{h}h</option>)}
        </select>
        <select value={selectedPDV} onChange={(e) => setSelectedPDV(e.target.value)} style={{ ...sel, minWidth: '150px' }} aria-label="Point de vente">
          <option value="all">Tous les points de vente</option>
          {(hourlyData?.by_pdv || []).map((p) => <option key={p.pdv_id} value={p.pdv_id}>{p.pdv_name}</option>)}
        </select>
      </div>
      {dates.length === 0 ? (
        <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Données non disponibles</div>
      ) : (
        <div style={{ overflowX: 'auto', borderRadius: '8px', border: '1px solid #dee2e6' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...thStyle, textAlign: 'left', padding: '14px 12px', position: 'sticky', left: 0, zIndex: 10 }}>Point de vente</th>
                {dates.map((date) => {
                  const d = new Date(`${date}T12:00:00`);
                  const isToday = d.toDateString() === todayStr;
                  return (
                    <th key={date} style={{ ...thStyle, background: isToday ? 'linear-gradient(135deg, #42a5f5 0%, #1976d2 100%)' : C.gradient, border: isToday ? '2px solid #ffd700' : 'none' }}>
                      <div>{String(d.getDate()).padStart(2, '0')}/{String(d.getMonth() + 1).padStart(2, '0')}</div>
                      <div style={{ fontSize: '11px', opacity: 0.9 }}>{d.toLocaleDateString('fr-FR', { weekday: 'short' })}</div>
                    </th>
                  );
                })}
                <th style={thStyle}>Total</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ padding: '14px 12px', color: '#1a1a1a', position: 'sticky', left: 0, background: '#f4f6f9', fontWeight: 500, borderRight: '1px solid #dee2e6' }}>{r.name}</td>
                  {dates.map((date) => {
                    const v = r.totals[date] || 0;
                    return (
                      <td key={date} style={{ padding: '12px 8px', textAlign: 'center', color: v > 0 ? '#1a1a1a' : '#bbb', fontWeight: v > 0 ? 600 : 400, fontSize: '14px' }}>
                        <span style={{ display: 'inline-block', padding: v > 0 ? '4px 8px' : '4px', borderRadius: '4px', background: v > 0 ? C.gradient : 'transparent', color: v > 0 ? '#fff' : 'inherit', boxShadow: v > 0 ? '0 2px 8px rgba(21,101,192,0.3)' : 'none' }}>{v}</span>
                      </td>
                    );
                  })}
                  <td style={{ padding: '14px 12px', textAlign: 'center', color: '#1565c0', fontWeight: 700, fontSize: '15px' }}>{r.total}</td>
                </tr>
              ))}
              <tr style={{ borderBottom: '2px solid #dee2e6', background: '#f0f6ff' }}>
                <td style={{ padding: '14px 12px', color: '#1565c0', fontWeight: 700, position: 'sticky', left: 0, background: '#e3f2fd', textTransform: 'uppercase', borderRight: '1px solid #dee2e6' }}>Total</td>
                {dates.map((date) => <td key={date} style={{ padding: '14px 8px', textAlign: 'center', color: '#333', fontWeight: 700, fontSize: '15px' }}>{colTotals[date]}</td>)}
                <td style={{ padding: '14px 12px', textAlign: 'center', color: '#1565c0', fontWeight: 800, fontSize: '16px' }}>{grand}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ============ SESSIONS ============

const STATUS_CFG = {
  active: { label: 'En cours', color: '#389e0d', bg: 'rgba(82, 196, 26, 0.15)' },
  completed: { label: 'Terminée', color: '#555', bg: '#f0f0f0' },
  pending: { label: 'Planifiée', color: '#1565c0', bg: '#e3f2fd' },
  cancelled: { label: 'Annulée', color: '#cf1322', bg: 'rgba(255, 77, 79, 0.12)' },
};

function SessionsContent({ setMessage }) {
  const [sessions, setSessions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await api.get('/palet/admin/sessions');
      setSessions(data);
    } catch {
      setMessage({ type: 'error', text: 'Erreur lors du chargement des sessions' });
    } finally {
      setLoading(false);
    }
  }, [setMessage]);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const handleToggleSession = async (session) => {
    const activate = session.status !== 'active';
    try {
      await api.put(`/palet/admin/sessions/${session.id}/${activate ? 'activate' : 'deactivate'}`);
      setMessage({ type: 'success', text: activate ? 'Session activée' : 'Session désactivée' });
      loadSessions();
    } catch (e) {
      setMessage({ type: 'error', text: errorMessage(e, 'Erreur lors du changement de statut') });
    }
  };

  const handleDeleteSession = async (session) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette session ?')) return;
    try {
      await api.delete(`/palet/admin/sessions/${session.id}`);
      setMessage({ type: 'success', text: 'Session supprimée avec succès' });
      loadSessions();
    } catch (e) {
      setMessage({ type: 'error', text: errorMessage(e, 'Erreur lors de la suppression') });
    }
  };

  const badge = (status) => {
    const c = STATUS_CFG[status] || STATUS_CFG.pending;
    return <span style={{ padding: '6px 16px', borderRadius: '20px', background: c.bg, color: c.color, fontSize: '13px', fontWeight: 600 }}>{c.label}</span>;
  };
  const when = (s) => {
    const a = new Date(s.start_time); const b = new Date(s.end_time);
    const t = (d) => d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return a.toDateString() === b.toDateString()
      ? `${a.toLocaleDateString('fr-FR')} ${t(a)} - ${t(b)}`
      : `${a.toLocaleDateString('fr-FR')} ${t(a)} → ${b.toLocaleDateString('fr-FR')} ${t(b)}`;
  };

  if (loading) return <div style={{ padding: '30px', textAlign: 'center', color: '#333' }}>Chargement...</div>;

  return (
    <>
      <PageHead icon="fa-calendar-alt" title="Sessions">
        <button onClick={() => { setSelectedSession(null); setShowModal(true); }} style={primaryBtn}>
          <i className="fas fa-plus"></i>Nouvelle Session
        </button>
      </PageHead>
      <div style={{ ...panel, padding: '24px' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #dee2e6' }}>
                {['Point de Vente', 'Session', 'Date & Horaire', 'Participations', 'Statut', 'Actions'].map((h) => <th key={h} style={th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {sessions.length === 0 && <tr><td colSpan="6" style={{ ...td, textAlign: 'center', color: '#999' }}>Aucune session.</td></tr>}
              {sessions.map((s) => (
                <tr key={s.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ ...td, color: '#1a1a1a', fontWeight: 600 }}>
                    {s.pdv_name || 'Non assigné'}
                    <div style={{ fontSize: '12px', color: '#888', fontWeight: 400 }}>{s.city_name}</div>
                  </td>
                  <td style={td}>{s.name}</td>
                  <td style={{ ...td, color: '#555', fontSize: '13px' }}>
                    {when(s)}
                    {s.status === 'active' && !s.in_dates && <div style={{ color: '#d4930a', fontSize: '12px' }}><i className="fas fa-clock me-1"></i>hors dates</div>}
                    {s.status === 'active' && s.in_dates && !s.can_open && <div style={{ color: '#cf1322', fontSize: '12px' }}><i className="fas fa-box-open me-1"></i>stock insuffisant</div>}
                  </td>
                  <td style={td}>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', background: '#e3f2fd', color: '#1565c0', fontSize: '12px', fontWeight: 600 }}>{s.rounds_count}</span>
                  </td>
                  <td style={td}>{badge(s.status)}</td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <button onClick={() => handleToggleSession(s)} title={s.status === 'active' ? 'Désactiver' : 'Activer'}
                        style={{ padding: '6px 12px', background: s.status === 'active' ? 'rgba(82,196,26,0.12)' : '#f9f9f9', color: s.status === 'active' ? '#389e0d' : '#cf1322', border: `1px solid ${s.status === 'active' ? '#52c41a' : '#ff4d4f'}`, borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        <i className={`fas fa-${s.status === 'active' ? 'toggle-on' : 'toggle-off'}`}></i>
                      </button>
                      <button onClick={() => { setSelectedSession(s); setShowModal(true); }} title="Modifier"
                        style={{ padding: '6px 12px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                        <i className="fas fa-edit"></i>
                      </button>
                      <button onClick={() => handleDeleteSession(s)} title="Supprimer" style={delBtn}><i className="fas fa-trash"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showModal && (
        <SessionModal session={selectedSession} onClose={() => setShowModal(false)}
          onSave={async () => { await loadSessions(); setShowModal(false); setMessage({ type: 'success', text: selectedSession ? 'Session modifiée' : 'Session créée' }); }} />
      )}
    </>
  );
}

function SessionModal({ session, onClose, onSave }) {
  const [pdvs, setPdvs] = useState([]);
  const [prizes, setPrizes] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: session?.name || '',
    pdv_id: session?.pdv_id || '',
    start_time: toLocalInput(session?.start_time),
    end_time: toLocalInput(session?.end_time),
    stock_allocations: session?.stock_allocations || {},
  });

  useEffect(() => {
    Promise.all([api.get('/admin/pdv'), api.get('/palet/admin/prizes')]).then(([p, z]) => {
      setPdvs(p.data.filter((x) => x.is_active !== false));
      const winning = z.data.filter((x) => !x.is_losing && x.is_active);
      setPrizes(winning);
      if (!session) setFormData((f) => ({ ...f, stock_allocations: Object.fromEntries(winning.map((x) => [x.id, 10])) }));
    }).catch((e) => setError(errorMessage(e)));
  }, [session]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    const body = {
      name: formData.name, pdv_id: parseInt(formData.pdv_id, 10),
      start_time: fromLocalInput(formData.start_time), end_time: fromLocalInput(formData.end_time),
      stock_allocations: Object.entries(formData.stock_allocations).map(([id, q]) => ({ prize_config_id: parseInt(id, 10), initial_quantity: parseInt(q, 10) || 0 })),
    };
    try {
      if (session) await api.put(`/palet/admin/sessions/${session.id}`, body);
      else await api.post('/palet/admin/sessions', body);
      onSave();
    } catch (err) {
      setError(errorMessage(err, 'Erreur lors de la création de la session'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalBack} onClick={onClose}>
      <div style={modalBox} role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>{session ? 'Modifier la Session' : 'Nouvelle Session'}</h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}><i className="fas fa-times"></i></button>
        </div>
        {error && <div className="alert alert-danger py-2">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={fieldLabel} htmlFor="s-pdv">Point de Vente *</label>
            <select id="s-pdv" value={formData.pdv_id} onChange={(e) => setFormData({ ...formData, pdv_id: e.target.value })} required style={input}
              disabled={!!session && session.rounds_count > 0}>
              <option value="">Sélectionner un point de vente</option>
              {pdvs.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.city?.name})</option>)}
            </select>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={fieldLabel} htmlFor="s-name">Nom de la session *</label>
            <input id="s-name" type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ex: Palet Octobre" required style={input} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div>
              <label style={fieldLabel} htmlFor="s-start">Début *</label>
              <input id="s-start" type="datetime-local" value={formData.start_time} onChange={(e) => setFormData({ ...formData, start_time: e.target.value })} required style={input} />
            </div>
            <div>
              <label style={fieldLabel} htmlFor="s-end">Fin *</label>
              <input id="s-end" type="datetime-local" value={formData.end_time} onChange={(e) => setFormData({ ...formData, end_time: e.target.value })} required style={input} />
            </div>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label style={{ ...fieldLabel, marginBottom: '12px' }}>Allocation des Stocks</label>
            {session && <p style={{ fontSize: '12px', color: '#888', marginTop: '-6px' }}>Stock total par lot : une modification est enregistrée comme un ajustement tracé.</p>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
              {prizes.map((p) => (
                <div key={p.id} style={{ border: '1px solid #dee2e6', borderRadius: '8px', padding: '12px', background: '#f4f6f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <div style={{ width: '24px', height: '24px', background: p.color, borderRadius: '50%', flexShrink: 0 }}></div>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1a1a1a' }}>{p.prize_label}<div style={{ fontSize: '11px', color: '#666' }}>{p.name}</div></div>
                  </div>
                  <input type="number" min="0" aria-label={`Stock ${p.prize_label}`} value={formData.stock_allocations[p.id] ?? 0}
                    onChange={(e) => setFormData({ ...formData, stock_allocations: { ...formData.stock_allocations, [p.id]: parseInt(e.target.value, 10) || 0 } })}
                    style={{ ...input, padding: '8px 12px' }} />
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '32px' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Annuler</button>
            <button type="submit" disabled={saving} style={submitBtn}>{saving ? 'Enregistrement...' : session ? 'Modifier' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ POINTS DE VENTE (comptes partagés de la plateforme) ============

function PDVsContent({ setMessage }) {
  const [pdvs, setPdvs] = useState([]);
  const [cities, setCities] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPDV, setSelectedPDV] = useState(null);
  const [loading, setLoading] = useState(true);
  const empty = { name: '', code: '', address: '', city_id: '', has_big_screen: false };
  const [formData, setFormData] = useState(empty);

  const loadPDVs = useCallback(async () => {
    try { setPdvs((await api.get('/admin/pdv')).data); }
    catch { setMessage({ type: 'error', text: 'Erreur lors du chargement des points de vente' }); }
    finally { setLoading(false); }
  }, [setMessage]);
  useEffect(() => {
    loadPDVs();
    api.get('/admin/cities').then((r) => setCities(r.data)).catch(() => {});
  }, [loadPDVs]);

  const handleSavePDV = async () => {
    try {
      const body = { ...formData, city_id: parseInt(formData.city_id, 10) };
      if (selectedPDV) { await api.put(`/admin/pdv/${selectedPDV.id}`, body); setMessage({ type: 'success', text: 'PDV mis à jour' }); }
      else { await api.post('/admin/pdv', body); setMessage({ type: 'success', text: 'PDV créé' }); }
      setShowModal(false);
      loadPDVs();
    } catch (e) { setMessage({ type: 'error', text: errorMessage(e, 'Erreur lors de la sauvegarde') }); setShowModal(false); }
  };

  const handleDeletePDV = async (pdv) => {
    if (!window.confirm('Supprimer ce point de vente ?')) return;
    try { await api.delete(`/admin/pdv/${pdv.id}`); setMessage({ type: 'success', text: 'PDV supprimé' }); loadPDVs(); }
    catch (e) { setMessage({ type: 'error', text: errorMessage(e, 'Erreur lors de la suppression') }); }
  };

  const inp = { width: '100%', padding: '10px', border: '1px solid #dee2e6', borderRadius: '8px', background: '#fff', color: '#1a1a1a', fontSize: '14px' };
  const lab = { color: '#333', marginBottom: '8px', display: 'block', fontSize: '14px', fontWeight: 600 };

  if (loading) return <div style={{ padding: '32px', color: '#333' }}>Chargement...</div>;

  return (
    <>
      <PageHead icon="fa-store" title="Points de Vente">
        <button onClick={() => { setSelectedPDV(null); setFormData(empty); setShowModal(true); }} style={primaryBtn}>
          <i className="fas fa-plus"></i>Nouveau PDV
        </button>
      </PageHead>
      <div style={{ ...panel, padding: '24px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>{['Nom', 'Code', 'Ville', 'Adresse', 'Statut', 'Actions'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {pdvs.map((pdv) => (
              <tr key={pdv.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ ...td, color: '#1a1a1a', fontWeight: 600 }}>{pdv.name}</td>
                <td style={td}>{pdv.code}</td>
                <td style={td}>{pdv.city?.name || '-'}</td>
                <td style={{ ...td, color: '#555' }}>{pdv.address || '-'}</td>
                <td style={td}>
                  <span style={{ padding: '6px 16px', borderRadius: '20px', background: pdv.is_active ? 'rgba(82,196,26,0.12)' : 'rgba(255,77,79,0.12)', color: pdv.is_active ? '#389e0d' : '#cf1322', fontSize: '13px', fontWeight: 600 }}>
                    {pdv.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button title="Modifier" style={editBtn} onClick={() => { setSelectedPDV(pdv); setFormData({ name: pdv.name, code: pdv.code, address: pdv.address || '', city_id: pdv.city_id?.toString() || '', has_big_screen: pdv.has_big_screen || false }); setShowModal(true); }}>
                      <i className="fas fa-edit"></i>
                    </button>
                    <button title="Supprimer" style={delBtn} onClick={() => handleDeletePDV(pdv)}><i className="fas fa-trash"></i></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {showModal && (
        <div style={modalBack} onClick={() => setShowModal(false)}>
          <div style={{ ...modalBox, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
            <h2 style={{ color: '#1565c0', marginBottom: '24px', fontSize: '20px' }}>{selectedPDV ? 'Modifier le PDV' : 'Nouveau Point de Vente'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div><label style={lab}>Nom *</label><input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} style={inp} /></div>
              <div><label style={lab}>Code *</label><input type="text" value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} style={inp} /></div>
              <div><label style={lab}>Ville *</label>
                <select value={formData.city_id} onChange={(e) => setFormData({ ...formData, city_id: e.target.value })} style={inp}>
                  <option value="">Sélectionner une ville</option>
                  {cities.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><label style={lab}>Adresse</label><input type="text" value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} style={inp} /></div>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#333', fontSize: '14px' }}>
                <input type="checkbox" checked={formData.has_big_screen} onChange={(e) => setFormData({ ...formData, has_big_screen: e.target.checked })} style={{ width: '18px', height: '18px' }} />
                Écran géant
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', background: '#f4f6f9', color: '#555', border: '1px solid #dee2e6', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>Annuler</button>
              <button onClick={handleSavePDV} style={{ padding: '10px 20px', background: C.gradient, color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 600 }}>{selectedPDV ? 'Mettre à jour' : 'Créer'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ============ LOTS ============

const EMPTY_PRIZE = { name: '', prize_label: '', description: '', icon: '', color: '#1565c0', weight: 10, is_losing: false, is_major_prize: false, is_active: true, segment_order: 0 };

function PrizesContent({ setMessage }) {
  const [prizes, setPrizes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const loadPrizes = useCallback(async () => {
    try { setPrizes((await api.get('/palet/admin/prizes')).data); }
    catch { setMessage({ type: 'error', text: 'Erreur lors du chargement des lots' }); }
    finally { setLoading(false); }
  }, [setMessage]);
  useEffect(() => { loadPrizes(); }, [loadPrizes]);

  const handleSave = async (data) => {
    try {
      const body = { ...data, weight: Number(data.weight), segment_order: Number(data.segment_order) || 0, icon: data.icon || null, description: data.description || null };
      if (data.id) await api.put(`/palet/admin/prizes/${data.id}`, body);
      else await api.post('/palet/admin/prizes', body);
      setMessage({ type: 'success', text: data.id ? 'Lot mis à jour avec succès' : 'Lot créé' });
      setEditing(null);
      loadPrizes();
    } catch (e) { setMessage({ type: 'error', text: errorMessage(e, 'Erreur lors de la mise à jour') }); }
  };

  if (loading) return <div style={{ padding: '32px', color: '#333' }}>Chargement...</div>;
  const totalWeight = prizes.filter((p) => p.is_active).reduce((s, p) => s + (p.weight || 0), 0);

  return (
    <>
      <PageHead icon="fa-gift" title="Configuration des Lots">
        <button onClick={() => setEditing({ ...EMPTY_PRIZE, segment_order: prizes.length })} style={primaryBtn}><i className="fas fa-plus"></i>Nouveau Lot</button>
      </PageHead>
      <div style={{ ...panel, padding: '24px', overflowX: 'auto' }}>
        <p style={{ fontSize: '13px', color: '#555', marginTop: 0 }}>
          À chaque lancer, trois cases sont remplies par tirage pondéré parmi les lots en stock. Le poids donne la fréquence relative d'un lot.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>{['Ordre', 'Image', 'Nom', 'Lot', 'Couleur', 'Poids', 'Type', 'Statut', 'Actions'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {prizes.map((p) => (
              <tr key={p.id} style={{ borderBottom: '1px solid #dee2e6', opacity: p.is_active ? 1 : 0.55 }}>
                <td style={{ ...td, fontWeight: 600, color: '#1a1a1a' }}>{p.segment_order}</td>
                <td style={td}><PrizeThumbs icon={p.icon} /></td>
                <td style={td}>{p.name}</td>
                <td style={td}>{p.prize_label}{p.is_major_prize && <i className="fas fa-star ms-2" style={{ color: '#faad14' }} title="Gros lot"></i>}</td>
                <td style={td}><div style={{ width: '30px', height: '30px', background: p.color, borderRadius: '50%' }}></div></td>
                <td style={td}>{p.weight}{p.is_active && totalWeight > 0 && <span style={{ color: '#999', fontSize: '12px' }}> ({Math.round((100 * p.weight) / totalWeight)}%)</span>}</td>
                <td style={td}>
                  <span style={{ padding: '4px 12px', borderRadius: '20px', background: p.is_losing ? '#f0f0f0' : '#e3f2fd', color: p.is_losing ? '#888' : '#1565c0', fontSize: '12px', fontWeight: 600 }}>
                    {p.is_losing ? 'Sans lot' : 'Gagnant'}
                  </span>
                </td>
                <td style={td}>{p.is_active ? 'Actif' : 'Inactif'}</td>
                <td style={td}>
                  <button onClick={() => setEditing({ ...EMPTY_PRIZE, ...p, icon: p.icon || '', description: p.description || '' })} style={{ ...editBtn, padding: '6px 16px', fontSize: '13px' }}>
                    <i className="fas fa-edit"></i> Modifier
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {editing && <PrizeEditModal prize={editing} onClose={() => setEditing(null)} onSave={handleSave} />}
    </>
  );
}

function PrizeEditModal({ prize, onClose, onSave }) {
  const [f, setF] = useState(prize);
  const inp = { width: '100%', padding: '12px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', background: '#fff', color: '#1a1a1a' };
  const row = { marginBottom: '16px' };
  return (
    <div style={modalBack} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: '500px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>{prize.id ? 'Modifier le Lot' : 'Nouveau Lot'}</h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}><i className="fas fa-times"></i></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSave(f); }}>
          <div style={row}><label style={fieldLabel}>Nom (interne)</label><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required style={inp} /></div>
          <div style={row}><label style={fieldLabel}>Lot affiché au client</label><input value={f.prize_label} onChange={(e) => setF({ ...f, prize_label: e.target.value })} required style={inp} /></div>
          <div style={row}><label style={fieldLabel}>Description</label><textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows="2" style={inp} /></div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', ...row }}>
            <div><label style={fieldLabel}>Poids</label><input type="number" min="0" step="any" value={f.weight} onChange={(e) => setF({ ...f, weight: e.target.value })} required style={inp} /></div>
            <div><label style={fieldLabel}>Ordre</label><input type="number" value={f.segment_order} onChange={(e) => setF({ ...f, segment_order: e.target.value })} style={inp} /></div>
            <div><label style={fieldLabel}>Couleur</label><input type="color" value={f.color || '#1565c0'} onChange={(e) => setF({ ...f, color: e.target.value })} style={{ ...inp, height: '48px', padding: '4px' }} /></div>
          </div>
          <div style={row}>
            <label style={fieldLabel}>Image du lot (facultatif)</label>
            <input value={f.icon} placeholder="/images/lots/polo-un-{gamme}.webp" onChange={(e) => setF({ ...f, icon: e.target.value })} style={inp} />
            <div style={{ fontSize: '12px', color: '#777', marginTop: '4px' }}>
              « {'{gamme}'} » est remplacé par <em>plate</em> (visuel La Source) ou <em>petillante</em> selon le jeu du client.
            </div>
            {f.icon && <div style={{ marginTop: '8px' }}><PrizeThumbs icon={f.icon} size={64} /></div>}
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', ...row }}>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '14px' }}>
              <input type="checkbox" checked={f.is_losing} disabled={!!prize.id} onChange={(e) => setF({ ...f, is_losing: e.target.checked })} /> Case « sans lot »
            </label>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '14px' }}>
              <input type="checkbox" checked={f.is_major_prize} onChange={(e) => setF({ ...f, is_major_prize: e.target.checked })} /> Gros lot
            </label>
            <label style={{ display: 'flex', gap: '6px', alignItems: 'center', fontSize: '14px' }}>
              <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} /> Actif
            </label>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Annuler</button>
            <button type="submit" style={submitBtn}>Enregistrer</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function PrizeThumbs({ icon, size = 40 }) {
  if (!icon) return <span style={{ color: '#bbb' }}>—</span>;
  const srcs = icon.includes('{gamme}') ? ['plate', 'petillante'].map((g) => icon.replace('{gamme}', g)) : [icon];
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {srcs.map((src) => (
        <img key={src} src={src} alt="" title={src} style={{ width: size, height: size, objectFit: 'contain', background: '#f4f6f9', borderRadius: '6px' }}
          onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      ))}
    </div>
  );
}

// ============ UTILISATEURS (comptes partagés de la plateforme) ============

const ROLE_COLORS = {
  super_admin: { bg: '#fff3e0', color: '#e65100', label: 'Super Admin' },
  admin: { bg: '#e3f2fd', color: '#1565c0', label: 'Admin' },
  hotesse: { bg: 'rgba(82,196,26,0.12)', color: '#389e0d', label: 'Hôtesse' },
};

function UsersContent({ setMessage }) {
  const [users, setUsers] = useState([]);
  const [pdvs, setPdvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [filterRole, setFilterRole] = useState('');
  const [filterPdv, setFilterPdv] = useState('');

  const loadUsers = useCallback(async () => {
    try { setUsers((await api.get('/admin/users')).data); }
    catch { setMessage({ type: 'error', text: 'Erreur lors du chargement des utilisateurs' }); }
    finally { setLoading(false); }
  }, [setMessage]);
  useEffect(() => {
    loadUsers();
    api.get('/admin/pdv').then((r) => setPdvs(r.data)).catch(() => {});
  }, [loadUsers]);

  const handleDelete = async (u) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try { await api.delete(`/admin/users/${u.id}`); setMessage({ type: 'success', text: 'Utilisateur supprimé' }); loadUsers(); }
    catch (e) { setMessage({ type: 'error', text: errorMessage(e, 'Erreur lors de la suppression') }); }
  };

  const filtered = users.filter((u) => (!filterRole || u.role === filterRole) && (!filterPdv || String(u.pdv_id) === filterPdv));
  const sel = { padding: '8px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '13px', color: '#333', background: '#fff', cursor: 'pointer', outline: 'none' };

  if (loading) return <div style={{ padding: '30px', textAlign: 'center', color: '#333' }}>Chargement...</div>;

  return (
    <>
      <PageHead icon="fa-user-cog" title="Utilisateurs">
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} style={sel} aria-label="Rôle">
            <option value="">Tous les rôles</option><option value="hotesse">Hôtesse</option><option value="admin">Admin</option><option value="super_admin">Super Admin</option>
          </select>
          <select value={filterPdv} onChange={(e) => setFilterPdv(e.target.value)} style={sel} aria-label="Point de vente">
            <option value="">Tous les PDV</option>
            {pdvs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          {(filterRole || filterPdv) && (
            <button onClick={() => { setFilterRole(''); setFilterPdv(''); }} style={{ ...delBtn, padding: '8px 14px', fontSize: '13px' }}><i className="fas fa-times"></i> Réinitialiser</button>
          )}
          <button onClick={() => { setSelectedUser(null); setShowModal(true); }} style={{ ...primaryBtn, padding: '10px 20px' }}><i className="fas fa-plus"></i>Nouvel Utilisateur</button>
        </div>
      </PageHead>
      <div style={{ ...panel, padding: '24px', overflowX: 'auto' }}>
        {(filterRole || filterPdv) && (
          <div style={{ marginBottom: '12px', fontSize: '13px', color: '#555' }}>
            <i className="fas fa-filter" style={{ marginRight: '6px', color: C.primary }}></i>{filtered.length} utilisateur{filtered.length !== 1 ? 's' : ''} trouvé{filtered.length !== 1 ? 's' : ''}
          </div>
        )}
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #dee2e6' }}>{['Nom complet', 'Username', 'Email', 'Rôle', 'Point de Vente', 'Statut', 'Actions'].map((h) => <th key={h} style={th}>{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((u) => {
              const rc = ROLE_COLORS[u.role] || { bg: '#f0f0f0', color: '#555', label: u.role };
              return (
                <tr key={u.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                  <td style={{ ...td, fontWeight: 600, color: '#1a1a1a' }}>{u.full_name || '-'}</td>
                  <td style={td}>{u.username}</td>
                  <td style={{ ...td, color: '#555', fontSize: '13px' }}>{u.email || '-'}</td>
                  <td style={td}><span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: rc.bg, color: rc.color }}>{rc.label}</span></td>
                  <td style={{ ...td, fontSize: '13px' }}>{u.pdv_name || '-'}</td>
                  <td style={td}>
                    <span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: u.is_active ? 'rgba(82,196,26,0.12)' : 'rgba(255,77,79,0.1)', color: u.is_active ? '#389e0d' : '#cf1322' }}>{u.is_active ? 'Actif' : 'Inactif'}</span>
                  </td>
                  <td style={td}>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button title="Modifier" style={editBtn} onClick={() => { setSelectedUser(u); setShowModal(true); }}><i className="fas fa-edit"></i></button>
                      <button title="Supprimer" style={delBtn} onClick={() => handleDelete(u)}><i className="fas fa-trash"></i></button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="7" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Aucun utilisateur</td></tr>}
          </tbody>
        </table>
      </div>
      {showModal && (
        <UserModal user={selectedUser} pdvs={pdvs} onClose={() => setShowModal(false)}
          onSave={async () => { await loadUsers(); setShowModal(false); setMessage({ type: 'success', text: selectedUser ? 'Utilisateur mis à jour' : 'Utilisateur créé avec succès' }); }}
          onError={(msg) => setMessage({ type: 'error', text: msg })} />
      )}
    </>
  );
}

function UserModal({ user, pdvs, onClose, onSave, onError }) {
  const [f, setF] = useState({
    username: user?.username || '', full_name: user?.full_name || '', email: user?.email || '', password: '',
    role: user?.role || 'hotesse', pdv_id: user?.pdv_id || '', is_active: user?.is_active !== undefined ? user.is_active : true,
  });
  const [saving, setSaving] = useState(false);
  const inp = { width: '100%', padding: '10px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', color: '#1a1a1a', background: '#fff', outline: 'none' };
  const lab = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: '#333' };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user && !f.password) { onError('Le mot de passe est obligatoire pour un nouvel utilisateur'); return; }
    setSaving(true);
    try {
      const payload = { ...f };
      if (!payload.email) delete payload.email;
      if (!payload.pdv_id) delete payload.pdv_id; else payload.pdv_id = parseInt(payload.pdv_id, 10);
      if (!payload.password) delete payload.password;
      if (user) await api.put(`/admin/users/${user.id}`, payload);
      else await api.post('/admin/users', payload);
      onSave();
    } catch (err) {
      onError(errorMessage(err, 'Erreur lors de la sauvegarde'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={modalBack} onClick={onClose}>
      <div style={{ ...modalBox, maxWidth: '480px' }} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: 600, color: '#1a1a1a' }}>{user ? "Modifier l'utilisateur" : 'Nouvel utilisateur'}</h2>
          <button onClick={onClose} aria-label="Fermer" style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: '20px', color: '#666' }}><i className="fas fa-times"></i></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div><label style={lab}>Nom complet</label><input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} placeholder="Jean Dupont" style={inp} /></div>
            <div><label style={lab}>Nom d'utilisateur *</label><input value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="jean.dupont" required style={inp} /></div>
            <div><label style={lab}>Email</label><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="jean@example.com" style={inp} /></div>
            <div><label style={lab}>{user ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe *'}</label>
              <input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} placeholder="••••••••" required={!user} style={inp} autoComplete="new-password" /></div>
            <div><label style={lab}>Rôle *</label>
              <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} required style={inp}>
                <option value="hotesse">Hôtesse</option><option value="admin">Admin</option><option value="super_admin">Super Admin</option>
              </select></div>
            <div><label style={lab}>Point de Vente</label>
              <select value={f.pdv_id} onChange={(e) => setF({ ...f, pdv_id: e.target.value })} style={inp}>
                <option value="">Aucun</option>
                {pdvs.map((p) => <option key={p.id} value={p.id}>{p.name}{p.city ? ` (${p.city.name})` : ''}</option>)}
              </select></div>
            {user && (
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', ...lab, margin: 0 }}>
                <input type="checkbox" checked={f.is_active} onChange={(e) => setF({ ...f, is_active: e.target.checked })} style={{ width: '18px', height: '18px' }} />Compte actif
              </label>
            )}
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '28px' }}>
            <button type="button" onClick={onClose} style={cancelBtn}>Annuler</button>
            <button type="submit" disabled={saving} style={submitBtn}>{saving ? 'Enregistrement...' : user ? 'Mettre à jour' : 'Créer'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============ PRODUITS ============

const EMPTY_PRODUCT = { name: '', line: 'plate', format_label: '1 L', min_quantity: 1, is_active: true, sort_order: 0 };

function ProductsContent({ setMessage }) {
  const [list, setList] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get('/palet/admin/products').then((r) => setList(r.data))
    .catch((e) => setMessage({ type: 'error', text: errorMessage(e) })), [setMessage]);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const body = { ...edit, min_quantity: Number(edit.min_quantity) || 1, sort_order: Number(edit.sort_order) || 0 };
      if (edit.id) await api.put(`/palet/admin/products/${edit.id}`, body);
      else await api.post('/palet/admin/products', body);
      setEdit(null);
      setMessage({ type: 'success', text: 'Produit enregistré.' });
      load();
    } catch (err) { setMessage({ type: 'error', text: errorMessage(err) }); }
  };
  const inp = { width: '100%', padding: '10px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', background: '#fff' };
  return (
    <>
      <PageHead icon="fa-bottle-water" title="Produits éligibles">
        <button onClick={() => setEdit({ ...EMPTY_PRODUCT })} style={primaryBtn}><i className="fas fa-plus"></i>Nouveau Produit</button>
      </PageHead>
      <div style={{ ...panel, padding: '24px', overflowX: 'auto' }}>
        <p style={{ fontSize: '13px', color: '#555', marginTop: 0 }}>
          L'hôtesse saisit les bouteilles achetées par produit ; le total donne le nombre de lancers (voir Réglages).
          La gamme majoritaire choisit l'habillage du jeu : bleu pour Plate, vert pour La Pétillante.
        </p>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '2px solid #dee2e6' }}>{['Produit', 'Gamme', 'Format', 'Ordre', 'Statut', 'Actions'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {list.map((p) => (
              <tr key={p.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ ...td, fontWeight: 600, color: '#1a1a1a' }}>{p.name}</td>
                <td style={td}><span style={{ padding: '4px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600, background: p.line === 'petillante' ? '#e6f5ec' : '#e3f2fd', color: p.line === 'petillante' ? '#0b5a3b' : '#1565c0' }}>{p.line === 'petillante' ? 'La Pétillante' : 'Plate'}</span></td>
                <td style={td}>{p.format_label || '-'}</td>
                <td style={td}>{p.sort_order}</td>
                <td style={td}>{p.is_active ? 'Actif' : 'Inactif'}</td>
                <td style={td}><button style={editBtn} onClick={() => setEdit({ ...p })}><i className="fas fa-edit"></i></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {edit && (
        <div style={modalBack} onClick={() => setEdit(null)}>
          <form style={{ ...modalBox, maxWidth: '480px' }} onClick={(e) => e.stopPropagation()} onSubmit={save}>
            <h2 style={{ margin: '0 0 20px', fontSize: '20px', fontWeight: 600 }}>{edit.id ? 'Modifier le produit' : 'Nouveau produit'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              <div><label style={fieldLabel}>Nom *</label><input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} required style={inp} /></div>
              <div><label style={fieldLabel}>Gamme</label>
                <select value={edit.line} onChange={(e) => setEdit({ ...edit, line: e.target.value })} style={inp}>
                  <option value="plate">Supermont Plate</option><option value="petillante">Supermont La Pétillante</option>
                </select></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div><label style={fieldLabel}>Format</label><input value={edit.format_label || ''} onChange={(e) => setEdit({ ...edit, format_label: e.target.value })} style={inp} /></div>
                <div><label style={fieldLabel}>Ordre</label><input type="number" value={edit.sort_order} onChange={(e) => setEdit({ ...edit, sort_order: e.target.value })} style={inp} /></div>
              </div>
              <label style={{ display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px' }}>
                <input type="checkbox" checked={edit.is_active} onChange={(e) => setEdit({ ...edit, is_active: e.target.checked })} /> Éligible au jeu
              </label>
            </div>
            <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
              <button type="button" onClick={() => setEdit(null)} style={cancelBtn}>Annuler</button>
              <button type="submit" style={submitBtn}>Enregistrer</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

// ============ JOURNAL ============

function JournalContent() {
  const [type, setType] = useState('');
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get('/palet/admin/events', { params: { event_type: type || undefined, limit: 300 } })
      .then((r) => setRows(r.data)).catch(() => {});
  }, [type]);
  const sel = { padding: '8px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '13px', background: '#fff' };
  return (
    <>
      <PageHead icon="fa-list-ul" title="Journal d'audit">
        <select value={type} onChange={(e) => setType(e.target.value)} style={sel} aria-label="Type d'événement">
          <option value="">Tous les événements</option>
          {Object.entries(EVENT_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </PageHead>
      <div style={{ ...panel, padding: '24px', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr style={{ borderBottom: '2px solid #dee2e6' }}>{['Date', 'Événement', 'Participation', 'PDV', 'Utilisateur', 'Détails'].map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan="6" style={{ ...td, textAlign: 'center', color: '#999' }}>Aucun événement.</td></tr>}
            {rows.map((e) => (
              <tr key={e.id} style={{ borderBottom: '1px solid #dee2e6' }}>
                <td style={{ ...td, whiteSpace: 'nowrap', fontSize: '13px' }}>{fmtDateTime(e.at)}</td>
                <td style={{ ...td, fontWeight: 600 }}>{EVENT_LABELS[e.type] || e.type}</td>
                <td style={{ ...td, fontFamily: 'monospace' }}>{e.round_code || ''}</td>
                <td style={td}>{e.pdv || ''}</td>
                <td style={td}>{e.user || ''}</td>
                <td style={{ ...td, color: '#777', fontSize: '12px' }}>{e.payload ? describe(e.payload) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ============ RÉGLAGES ============

const SETTING_LABELS = {
  bottles_per_play: ['Bouteilles 1 L par lancer', 'Ex. 2 : 2 bouteilles = 1 lancer, 4 bouteilles = 2 lancers.'],
  max_plays_per_purchase: ['Nombre maximum de lancers pour un achat', ''],
  require_ticket_photo: ['Photo du ticket de caisse obligatoire', ''],
  require_ticket_ref: ['Numéro du ticket de caisse obligatoire', 'Un même numéro ne sert alors qu’une fois par point de vente.'],
  reservation_ttl_minutes: ['Durée de réservation des lots d’un lancer non joué (minutes)', 'Au-delà, les lots retournent en stock ; de nouvelles cases sont tirées si le client revient.'],
  distinct_boxes: ['Trois lots différents par lancer quand le stock le permet', ''],
  open_alert_minutes: ['Alerte « lancer non joué » après (minutes)', ''],
  undelivered_alert_hours: ['Alerte « lot non remis » après (heures)', ''],
  timezone: ['Fuseau horaire des journées', 'ex. Africa/Douala'],
};

function SettingsContent({ setMessage }) {
  const [s, setS] = useState(null);
  useEffect(() => {
    api.get('/palet/admin/settings').then((r) => setS(r.data)).catch((e) => setMessage({ type: 'error', text: errorMessage(e) }));
  }, [setMessage]);
  const save = async (e) => {
    e.preventDefault();
    try { setS((await api.put('/palet/admin/settings', s)).data); setMessage({ type: 'success', text: 'Réglages enregistrés.' }); }
    catch (err) { setMessage({ type: 'error', text: errorMessage(err) }); }
  };
  if (!s) return null;
  const inp = { width: '100%', maxWidth: '320px', padding: '10px 14px', border: '1px solid #dee2e6', borderRadius: '8px', fontSize: '14px', background: '#fff' };
  return (
    <>
      <PageHead icon="fa-sliders" title="Réglages" />
      <form style={{ ...panel, padding: '24px' }} onSubmit={save}>
        {Object.entries(SETTING_LABELS).map(([k, [label, hint]]) => (
          <div key={k} style={{ marginBottom: '18px' }}>
            {typeof s[k] === 'boolean' ? (
              <label style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', fontSize: '14px', fontWeight: 600, color: '#333' }}>
                <input type="checkbox" checked={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.checked })} style={{ width: '18px', height: '18px', marginTop: '2px' }} />
                <span>{label}{hint && <div style={{ fontWeight: 400, color: '#777', fontSize: '12px' }}>{hint}</div>}</span>
              </label>
            ) : (
              <>
                <label style={fieldLabel} htmlFor={`set-${k}`}>{label}</label>
                <input id={`set-${k}`} value={s[k]} onChange={(e) => setS({ ...s, [k]: e.target.value })} style={inp} />
                {hint && <div style={{ color: '#777', fontSize: '12px', marginTop: '4px' }}>{hint}</div>}
              </>
            )}
          </div>
        ))}
        <button type="submit" style={{ ...primaryBtn, display: 'inline-flex' }}><i className="fas fa-save"></i>Enregistrer</button>
      </form>
    </>
  );
}
